import { c as _c } from "react/compiler-runtime";
import { IcoChevronDown16, IcoDotsHorizontal16, IcoFileText16 } from '@onefootprint/icons';
import type { ArtifactVersion, OrgArtifact } from '@onefootprint/request-types/dashboard';
import { Button, Dropdown, IconButton, Tooltip, useToast } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import type { RefObject } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMobileLayout from '@/hooks/use-mobile-layout';
import downloadArtifactFile from '@/utils/download-artifact';
import getArtifactIcon from '@/utils/get-artifact-icon';
import { exportArtifactToPdf } from '../../utils/export-pdf';
import ArtifactRenameDialog from '../header/rename-dialog';
import VersionsHistoryDialog from '../header/versions-history-dialog';
type ContentHeaderProps = {
  /** null = the Generated Summary view; otherwise the selected artifact/judgment. */
  artifact: OrgArtifact | null;
  artifactContent: string | undefined;
  versions: ArtifactVersion[];
  onSelectVersion: (versionId: string) => void;
  onRenameArtifact: (artifactId: string, newName: string) => void;
  onDeleteArtifact: (artifactId: string) => void;
  markdownContentRef: RefObject<HTMLDivElement | null>;
  isMarkdownDirty: boolean;
  isRestoring: boolean;
  isSaveDisabled: boolean;
  isRenameDisabled: boolean;
  isSaving: boolean;
  onSaveEdits: () => void;
  onDiscardEdits: () => void;
  // Generated Summary export (the report is the gen-ui blocks).
  onExportPdf: () => void;
  isExporting: boolean;
  isExportDisabled: boolean;
  /** Set where markdown opens read-only, on a phone or for an agent's report, until the reader asks to edit it. */
  onStartEditing?: () => void;
  /** Set once that markdown is open in its editor, to go back to reading it. */
  onStopEditing?: () => void;
  /** Set only on a phone, where the title opens the artifact list. */
  onToggleSidebar?: () => void;
  isSidebarOpen?: boolean;
};
const ContentHeader = props => {
  const $ = _c(33);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "artifactSidebar"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("artifact", t0);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  let t1;
  if ($[1] !== props.artifact || $[2] !== t) {
    t1 = props.artifact ? props.artifact.name : t(_temp);
    $[1] = props.artifact;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const title = t1;
  let t2;
  if ($[4] !== props.artifact) {
    t2 = props.artifact ? getArtifactIcon(props.artifact.mimeType) : IcoFileText16;
    $[4] = props.artifact;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const TitleIcon = t2;
  const isMarkdown = props.artifact?.mimeType === "text/markdown";
  let t3;
  if ($[6] !== props) {
    t3 = newName => {
      if (!props.artifact) {
        return;
      }
      props.onRenameArtifact(props.artifact.id, newName);
      setShowRenameDialog(false);
    };
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleRenameSubmit = t3;
  let t4;
  if ($[8] !== TitleIcon || $[9] !== props.isSidebarOpen || $[10] !== props.onToggleSidebar || $[11] !== t || $[12] !== title) {
    t4 = props.onToggleSidebar ? <h2 className="flex flex-1 min-w-0"><button type="button" aria-expanded={!!props.isSidebarOpen} aria-label={`${t(_temp2)}: ${title}`} onClick={props.onToggleSidebar} className="-ml-2 inline-flex h-10 max-w-full items-center gap-2 rounded-md px-2 text-label-2 font-semibold text-primary active:bg-hover"><TitleIcon className="shrink-0" color="primary" /><span className="truncate">{title}</span><IcoChevronDown16 className={cx("shrink-0 transition-transform duration-200 ease-out", props.isSidebarOpen && "rotate-180")} /></button></h2> : <div className="flex flex-1 items-center gap-2 min-w-0"><TitleIcon className="shrink-0" color="primary" /><h2 className="text-label-2 font-semibold text-primary truncate">{title}</h2></div>;
    $[8] = TitleIcon;
    $[9] = props.isSidebarOpen;
    $[10] = props.onToggleSidebar;
    $[11] = t;
    $[12] = title;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setShowRenameDialog(true);
    t6 = () => setShowVersions(true);
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  let t7;
  if ($[16] !== props) {
    t7 = <div className="flex items-center gap-2 shrink-0"><Actions {...props} onShowRenameDialog={t5} onShowVersions={t6} /></div>;
    $[16] = props;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t4 || $[19] !== t7) {
    t8 = <div className="max-w-(--main-content-max-width) mx-auto w-full px-4 md:px-16 min-h-12 md:min-h-11 py-1 md:py-0 flex flex-wrap items-center justify-between gap-2 md:gap-3">{t4}{t7}</div>;
    $[18] = t4;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== handleRenameSubmit || $[22] !== isMarkdown || $[23] !== props.artifact || $[24] !== props.isSaving || $[25] !== props.onSelectVersion || $[26] !== props.versions || $[27] !== showRenameDialog || $[28] !== showVersions) {
    t9 = props.artifact && <><ArtifactRenameDialog open={showRenameDialog} onClose={() => setShowRenameDialog(false)} initialName={props.artifact.name} isSaving={props.isSaving} onRename={handleRenameSubmit} /><VersionsHistoryDialog open={showVersions} onClose={() => setShowVersions(false)} versions={props.versions} onSelectVersion={isMarkdown ? props.onSelectVersion : undefined} /></>;
    $[21] = handleRenameSubmit;
    $[22] = isMarkdown;
    $[23] = props.artifact;
    $[24] = props.isSaving;
    $[25] = props.onSelectVersion;
    $[26] = props.versions;
    $[27] = showRenameDialog;
    $[28] = showVersions;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t8 || $[31] !== t9) {
    t10 = <div className="shrink-0 border-b border-tertiary bg-secondary">{t8}{t9}</div>;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
};
const Actions = props => {
  const $ = _c(48);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "header.actions"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t0);
  const {
    t: tCommon
  } = useTranslation("common");
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "versionsHistory"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    t: tVersions
  } = useTranslation("artifact", t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "actions.editControls"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    t: tEdit
  } = useTranslation("artifact", t2);
  const isMobile = useMobileLayout();
  if (!props.artifact) {
    if (isMobile) {
      return null;
    }
    const t3 = props.onExportPdf;
    const t4 = props.isExportDisabled || props.isExporting;
    const t5 = props.isExporting;
    let t6;
    if ($[3] !== t) {
      t6 = t(_temp3);
      $[3] = t;
      $[4] = t6;
    } else {
      t6 = $[4];
    }
    let t7;
    if ($[5] !== props.isExporting || $[6] !== props.onExportPdf || $[7] !== t4 || $[8] !== t6) {
      t7 = <Button size="compact" variant="secondary" onClick={t3} disabled={t4} loading={t5}>{t6}</Button>;
      $[5] = props.isExporting;
      $[6] = props.onExportPdf;
      $[7] = t4;
      $[8] = t6;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return t7;
  }
  if (props.isRestoring || props.isMarkdownDirty) {
    const t3 = isMobile ? "default" : "compact";
    let t4;
    if ($[10] !== props.isRestoring || $[11] !== props.isSaveDisabled || $[12] !== props.isSaving || $[13] !== props.onDiscardEdits || $[14] !== props.onSaveEdits || $[15] !== t3) {
      t4 = <SaveAndDiscardButtons size={t3} isSaveDisabled={props.isSaveDisabled} isSaving={props.isSaving} isRestoring={props.isRestoring} onSaveEdits={props.onSaveEdits} onDiscardEdits={props.onDiscardEdits} />;
      $[10] = props.isRestoring;
      $[11] = props.isSaveDisabled;
      $[12] = props.isSaving;
      $[13] = props.onDiscardEdits;
      $[14] = props.onSaveEdits;
      $[15] = t3;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    return t4;
  }
  if (props.artifactContent === undefined) {
    return null;
  }
  const {
    artifact,
    artifactContent
  } = props;
  const isMarkdown = artifact.mimeType === "text/markdown";
  let t3;
  if ($[17] !== isMarkdown || $[18] !== isMobile || $[19] !== props.onStartEditing || $[20] !== tCommon) {
    t3 = isMarkdown && props.onStartEditing && <Button size={isMobile ? "default" : "compact"} variant="secondary" onClick={props.onStartEditing}>{tCommon(_temp4)}</Button>;
    $[17] = isMarkdown;
    $[18] = isMobile;
    $[19] = props.onStartEditing;
    $[20] = tCommon;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== isMarkdown || $[23] !== isMobile || $[24] !== props.onStopEditing || $[25] !== tEdit) {
    t4 = isMarkdown && props.onStopEditing && <Button size={isMobile ? "default" : "compact"} variant="secondary" onClick={props.onStopEditing}>{tEdit(_temp5)}</Button>;
    $[22] = isMarkdown;
    $[23] = isMobile;
    $[24] = props.onStopEditing;
    $[25] = tEdit;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  let t5;
  if ($[27] !== isMobile || $[28] !== props.onShowVersions || $[29] !== tVersions) {
    t5 = !isMobile && <Button aria-label={tVersions(_temp6)} size="compact" variant="secondary" onClick={props.onShowVersions}>{tVersions(_temp7)}</Button>;
    $[27] = isMobile;
    $[28] = props.onShowVersions;
    $[29] = tVersions;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  const t6 = isMobile ? props.onShowVersions : undefined;
  let t7;
  if ($[31] !== artifact.id || $[32] !== props) {
    t7 = () => props.onDeleteArtifact(artifact.id);
    $[31] = artifact.id;
    $[32] = props;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== artifact || $[35] !== artifactContent || $[36] !== props.isRenameDisabled || $[37] !== props.isSaving || $[38] !== props.markdownContentRef || $[39] !== props.onShowRenameDialog || $[40] !== t6 || $[41] !== t7) {
    t8 = <ArtifactActionsMenu artifact={artifact} contentBase64={artifactContent} markdownContentRef={props.markdownContentRef} isRenameDisabled={props.isRenameDisabled} isSaving={props.isSaving} onRenameClick={props.onShowRenameDialog} onShowVersions={t6} onDelete={t7} />;
    $[34] = artifact;
    $[35] = artifactContent;
    $[36] = props.isRenameDisabled;
    $[37] = props.isSaving;
    $[38] = props.markdownContentRef;
    $[39] = props.onShowRenameDialog;
    $[40] = t6;
    $[41] = t7;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  let t9;
  if ($[43] !== t3 || $[44] !== t4 || $[45] !== t5 || $[46] !== t8) {
    t9 = <>{t3}{t4}{t5}{t8}</>;
    $[43] = t3;
    $[44] = t4;
    $[45] = t5;
    $[46] = t8;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  return t9;
};
type ArtifactMenuProps = {
  artifact: OrgArtifact;
  contentBase64: string;
  markdownContentRef: RefObject<HTMLDivElement | null>;
  isRenameDisabled: boolean;
  isSaving: boolean;
  onRenameClick: () => void;
  onShowVersions?: () => void;
  onDelete: () => void;
};
const ArtifactActionsMenu = props => {
  const $ = _c(15);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "actions.menu"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("artifact", t0);
  let t1;
  if ($[1] !== t) {
    t1 = t(_temp8);
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t(_temp9);
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <IconButton aria-label={t2} icon={IcoDotsHorizontal16} size="compact" variant="outline" className="max-md:size-8" />;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <Dropdown.Trigger asChild={true} ariaLabel={t1}>{t3}</Dropdown.Trigger>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== props) {
    t5 = <Dropdown.Portal><Dropdown.Content align="end" className="min-w-[220px]"><ArtifactMenuItems {...props} /></Dropdown.Content></Dropdown.Portal>;
    $[10] = props;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <Dropdown.Root modal={false}>{t4}{t5}</Dropdown.Root>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};

// Version history (phones only) / rename / download / delete rows for the document `…` menu. The
// host owns the dialogs — they must outlive this menu's Dropdown.Content, which unmounts on close.
const ArtifactMenuItems = ({
  artifact,
  contentBase64,
  markdownContentRef,
  isRenameDisabled,
  isSaving,
  onRenameClick,
  onShowVersions,
  onDelete
}: ArtifactMenuProps) => {
  const {
    t
  } = useTranslation('artifact', {
    keyPrefix: 'actions'
  });
  const {
    t: tVersions
  } = useTranslation('artifact', {
    keyPrefix: 'versionsHistory'
  });
  const {
    toast
  } = useToast();
  const isMobile = useMobileLayout();
  // Like the report export, a phone would rasterize its mobile layout, so it downloads the markdown file.
  const canDownloadAsPdf = artifact.mimeType === 'text/markdown' && !isMobile;
  const showErrorToast = () => toast({
    variant: 'error',
    title: t($ => $.errorToast.title),
    description: t($_0 => $_0.errorToast.description)
  });
  const handleDownload = () => {
    try {
      downloadArtifactFile({
        contentBase64,
        mimeType: artifact.mimeType,
        fileName: artifact.name
      });
    } catch {
      showErrorToast();
    }
  };
  const handleDownloadAsPdf = async () => {
    const el = markdownContentRef.current;
    if (!el) {
      showErrorToast();
      return;
    }
    // Yield two frames so the menu closes and repaints before rasterizing blocks the main thread.
    await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    try {
      const pdfFileName = artifact.name.replace(/\.md$/i, '.pdf') || 'artifact.pdf';
      await exportArtifactToPdf(el, pdfFileName);
    } catch {
      showErrorToast();
    }
  };
  return <Dropdown.Group>
      <Dropdown.GroupTitle>{t($_1 => $_1.menu.sectionTitle)}</Dropdown.GroupTitle>
      {onShowVersions && <Dropdown.Item size="compact" onSelect={onShowVersions} className="max-md:h-10">
          {tVersions($_2 => $_2.title)}
        </Dropdown.Item>}
      <Dropdown.Item size="compact" onSelect={onRenameClick} disabled={isRenameDisabled || isSaving} className={cx('max-md:h-10', {
      'text-tertiary cursor-not-allowed!': isRenameDisabled
    })} title={isRenameDisabled ? t($_3 => $_3.menu.renameBlankDisabled) : undefined}>
        {t($_4 => $_4.menu.rename)}
      </Dropdown.Item>
      {canDownloadAsPdf ? <Dropdown.Item size="compact" onSelect={handleDownloadAsPdf} className="max-md:h-10">
          {t($_5 => $_5.menu.downloadAsPdf)}
        </Dropdown.Item> : <Dropdown.Item size="compact" onSelect={handleDownload} className="max-md:h-10">
          {t($_6 => $_6.menu.download)}
        </Dropdown.Item>}
      <Dropdown.Item size="compact" destructive onSelect={onDelete} className="max-md:h-10">
        {t($_7 => $_7.menu.delete)}
      </Dropdown.Item>
    </Dropdown.Group>;
};

// Save + Discard for a dirty markdown editor.
// When restoring a version, "Save" reads "Restore this version" and Discard reads "Cancel".
const SaveAndDiscardButtons = t0 => {
  const $ = _c(27);
  const {
    size,
    isSaveDisabled,
    isSaving,
    isRestoring,
    onSaveEdits,
    onDiscardEdits
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "actions"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("artifact", t1);
  let t2;
  if ($[1] !== isRestoring || $[2] !== t) {
    t2 = isRestoring ? t(_temp20) : t(_temp21);
    $[1] = isRestoring;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isSaving || $[5] !== onDiscardEdits || $[6] !== size || $[7] !== t2) {
    t3 = <Button size={size} variant="secondary" onClick={onDiscardEdits} disabled={isSaving}>{t2}</Button>;
    $[4] = isSaving;
    $[5] = onDiscardEdits;
    $[6] = size;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t) {
    t4 = t(_temp22);
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = !isSaveDisabled;
  const t6 = isSaveDisabled || isSaving;
  let t7;
  if ($[11] !== isRestoring || $[12] !== t) {
    t7 = isRestoring ? t(_temp23) : t(_temp24);
    $[11] = isRestoring;
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== isSaving || $[15] !== onSaveEdits || $[16] !== size || $[17] !== t6 || $[18] !== t7) {
    t8 = <span><Button size={size} variant="primary" onClick={onSaveEdits} disabled={t6} loading={isSaving}>{t7}</Button></span>;
    $[14] = isSaving;
    $[15] = onSaveEdits;
    $[16] = size;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t4 || $[21] !== t5 || $[22] !== t8) {
    t9 = <Tooltip content={t4} disabled={t5}>{t8}</Tooltip>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t3 || $[25] !== t9) {
    t10 = <>{t3}{t9}</>;
    $[24] = t3;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
};
export default ContentHeader;
function _temp($) {
  return $.generatedSummary;
}
function _temp2($_0) {
  return $_0.ariaLabel;
}
function _temp3($) {
  return $.exportPdf;
}
function _temp4($_0) {
  return $_0.edit;
}
function _temp5($_1) {
  return $_1.done;
}
function _temp6($_2) {
  return $_2.triggerAriaLabel;
}
function _temp7($_3) {
  return $_3.title;
}
function _temp8($) {
  return $.ariaLabel;
}
function _temp9($_0) {
  return $_0.ariaLabel;
}
function _temp20($) {
  return $.editControls.cancel;
}
function _temp21($_0) {
  return $_0.editControls.discard;
}
function _temp22($_1) {
  return $_1.editControls.saveBlankDisabled;
}
function _temp23($_2) {
  return $_2.editControls.restore;
}
function _temp24($_3) {
  return $_3.editControls.save;
}